import { z } from "zod";
import { ONBOARDING_MAX_EMPLOYEE_ROWS } from "./onboarding-draft";

// ---------------------------------------------------------------------------
// Employee CSV import (GRA-172)
//
// Pure parsing, validation and team suggestion, shared by the webapp (which
// parses the file in the browser so the table is instant) and business-api
// (which re-validates the rows it is asked to import). Spec §17: UTF-8 with
// BOM, ";" or "," detected, columns mapped by header whatever the order or
// case, name + valid email + team required, mobile and employee number
// optional, duplicates on email merged.
// ---------------------------------------------------------------------------

export const EMPLOYEE_IMPORT_COLUMNS = [
  "name",
  "email",
  "mobile",
  "team",
  "role",
  "employeeNumber",
] as const;
export type EmployeeImportColumn = (typeof EMPLOYEE_IMPORT_COLUMNS)[number];

/** Header spellings per column, compared after `normalizeHeader`. */
export const EMPLOYEE_HEADER_ALIASES: Record<EmployeeImportColumn, readonly string[]> = {
  name: ["namn", "name", "förnamn efternamn", "fullständigt namn", "fullt namn", "medarbetare"],
  email: ["e-post", "epost", "email", "e-mail", "mejl", "mail", "e-postadress"],
  mobile: ["mobil", "mobilnummer", "telefon", "tel", "mobile", "phone", "telefonnummer"],
  team: ["avdelning", "team", "avd", "department", "säljteam", "grupp", "arbetsgrupp", "enhet"],
  role: ["roll", "befattning", "titel", "title", "role", "position"],
  employeeNumber: [
    "anställningsnummer",
    "anst.nr",
    "anstnr",
    "anst nr",
    "anställningsnr",
    "employee number",
    "employeeid",
    "employee id",
    "personalnummer",
    "id",
  ],
};

export interface EmployeeImportRow {
  /** Stable row identity for the editable table; not stored on the member. */
  id: string;
  name: string;
  email: string;
  mobile?: string;
  team: string;
  role?: string;
  employeeNumber?: string;
}

export type EmployeeRowErrorCode = "missing" | "invalidEmail" | "invalidMobile";
export type EmployeeRowErrors = Partial<
  Record<"name" | "email" | "team" | "mobile", EmployeeRowErrorCode>
>;

/**
 * The same rule `EmployeeImportRowSchema.email` enforces server-side. Sharing
 * it here means a row the table marks valid never fails re-validation on
 * import with no error for the UI to show (BugBot 8fc8a651).
 */
const EMAIL_SCHEMA = z.string().trim().email();
/** Swedish mobile numbers once separators are removed: 07x or +467x and eight digits. */
const MOBILE_RE = /^(\+46|0)7\d{8}$/;

export function normalizeHeader(header: string): string {
  return header
    .replace(/^\uFEFF/, "")
    .replace(/^["']+|["']+$/g, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ")
    .replace(/[.:]+$/, "");
}

export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

export function isValidMobile(mobile: string): boolean {
  return MOBILE_RE.test(mobile.replace(/[\s-]/g, ""));
}

/** ";" when the header has at least as many semicolons as commas (Swedish exports), else ",". */
export function detectSeparator(headerLine: string): ";" | "," {
  const semicolons = headerLine.match(/;/g)?.length ?? 0;
  const commas = headerLine.match(/,/g)?.length ?? 0;
  return semicolons >= commas ? ";" : ",";
}

/** One line into cells; quotes wrap cells with separators, "" is an escaped quote. */
export function splitCsvLine(line: string, sep: string): string[] {
  const out: string[] = [];
  let cur = "";
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]!;
    if (ch === '"') {
      if (quoted && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else quoted = !quoted;
    } else if (ch === sep && !quoted) {
      out.push(cur);
      cur = "";
    } else cur += ch;
  }
  out.push(cur);
  return out.map((c) => c.trim());
}

export interface HeaderMapping {
  map: Partial<Record<EmployeeImportColumn, number>>;
  unmapped: string[];
}

/** Columns by header text, whatever the order or case; the first match wins. */
export function mapHeaders(headers: string[]): HeaderMapping {
  const map: Partial<Record<EmployeeImportColumn, number>> = {};
  const unmapped: string[] = [];
  headers.forEach((raw, index) => {
    const header = normalizeHeader(raw);
    const column = EMPLOYEE_IMPORT_COLUMNS.find(
      (col) => map[col] === undefined && EMPLOYEE_HEADER_ALIASES[col].includes(header),
    );
    if (column) map[column] = index;
    else if (header) unmapped.push(raw.trim());
  });
  return { map, unmapped };
}

export function validateEmployeeRow(row: {
  name: string;
  email: string;
  team: string;
  mobile?: string;
}): EmployeeRowErrors {
  const errors: EmployeeRowErrors = {};
  if (!row.name.trim()) errors.name = "missing";
  if (!row.email.trim()) errors.email = "missing";
  else if (!EMAIL_SCHEMA.safeParse(row.email.trim()).success) errors.email = "invalidEmail";
  if (!row.team.trim()) errors.team = "missing";
  if (row.mobile?.trim() && !isValidMobile(row.mobile)) errors.mobile = "invalidMobile";
  return errors;
}

export interface ParsedEmployeeCsv {
  rows: EmployeeImportRow[];
  headerMap: HeaderMapping["map"];
  unmappedHeaders: string[];
  separator: ";" | ",";
  /** Lines that were entirely empty, not counted as rows. */
  skippedEmptyLines: number;
  /** The header had none of the required columns; nothing can be imported. */
  missingRequiredColumns: ("name" | "email" | "team")[];
}

export function parseEmployeeCsv(
  text: string,
  opts: { makeId?: (index: number) => string } = {},
): ParsedEmployeeCsv {
  const makeId = opts.makeId ?? ((index) => `row-${index + 1}`);
  const lines = text.replace(/^\uFEFF/, "").split(/\r?\n/);
  const nonEmpty = lines.filter((line) => line.trim() !== "");
  const skippedEmptyLines = lines.length - nonEmpty.length;
  const headerLine = nonEmpty[0] ?? "";
  const separator = detectSeparator(headerLine);
  const { map, unmapped } = mapHeaders(splitCsvLine(headerLine, separator));
  const missingRequiredColumns = (["name", "email", "team"] as const).filter(
    (col) => map[col] === undefined,
  );
  const cell = (cells: string[], col: EmployeeImportColumn): string | undefined => {
    const index = map[col];
    if (index === undefined) return undefined;
    return cells[index] ?? "";
  };
  const rows: EmployeeImportRow[] = nonEmpty.slice(1).map((line, index) => {
    const cells = splitCsvLine(line, separator);
    const optional = (col: EmployeeImportColumn) => {
      const value = cell(cells, col);
      return value ? value : undefined;
    };
    return {
      id: makeId(index),
      name: cell(cells, "name") ?? "",
      email: cell(cells, "email") ?? "",
      mobile: optional("mobile"),
      team: cell(cells, "team") ?? "",
      role: optional("role"),
      employeeNumber: optional("employeeNumber"),
    };
  });
  return { rows, headerMap: map, unmappedHeaders: unmapped, separator, skippedEmptyLines, missingRequiredColumns };
}

/**
 * Rows with the same email (case-insensitive) collapse into the first one;
 * a later row only fills fields the first left empty. Rows without an email
 * are kept as they are (validation flags them).
 */
export function mergeDuplicateEmails<T extends EmployeeImportRow>(
  rows: T[],
): { rows: T[]; merged: number } {
  const byEmail = new Map<string, T>();
  const out: T[] = [];
  let merged = 0;
  for (const row of rows) {
    const key = normalizeEmail(row.email);
    const first = key ? byEmail.get(key) : undefined;
    if (!first) {
      const copy = { ...row };
      out.push(copy);
      if (key) byEmail.set(key, copy);
      continue;
    }
    merged++;
    for (const field of ["name", "mobile", "team", "role", "employeeNumber"] as const) {
      if (!first[field]?.toString().trim() && row[field]?.toString().trim()) {
        (first as Record<string, unknown>)[field] = row[field];
      }
    }
  }
  return { rows: out, merged };
}

export interface TeamSuggestion {
  /** The raw team value as typed, trimmed; the key the admin's rename maps from. */
  key: string;
  memberIds: string[];
}

/** Distinct team values in order of first appearance, matched case-insensitively. */
export function suggestTeams(rows: EmployeeImportRow[]): TeamSuggestion[] {
  const byLower = new Map<string, TeamSuggestion>();
  for (const row of rows) {
    const key = row.team.trim();
    if (!key) continue;
    const lower = key.toLowerCase();
    const existing = byLower.get(lower);
    if (existing) existing.memberIds.push(row.id);
    else byLower.set(lower, { key, memberIds: [row.id] });
  }
  return Array.from(byLower.values());
}

export interface ResolvedTeam {
  /** The display name the team is created with. */
  name: string;
  memberIds: string[];
  /** The raw keys that ended up in this team (more than one when renamed alike). */
  sources: string[];
}

/**
 * Applies the admin's renames (`names[rawKey]`) and merges teams whose
 * effective names match case-insensitively; order of first appearance.
 */
export function resolveTeams(
  rows: EmployeeImportRow[],
  names: Record<string, string> = {},
): ResolvedTeam[] {
  const byLower = new Map<string, ResolvedTeam>();
  for (const suggestion of suggestTeams(rows)) {
    const renamed = names[suggestion.key]?.trim();
    const name = renamed || suggestion.key;
    const lower = name.toLowerCase();
    const existing = byLower.get(lower);
    if (existing) {
      existing.memberIds.push(...suggestion.memberIds);
      existing.sources.push(suggestion.key);
    } else {
      byLower.set(lower, { name, memberIds: [...suggestion.memberIds], sources: [suggestion.key] });
    }
  }
  return Array.from(byLower.values());
}

// ── Server input ───────────────────────────────────────────────────────────

export const EmployeeImportRowSchema = z.object({
  id: z.string().min(1).max(64),
  name: z.string().trim().min(1).max(160),
  email: z.string().trim().email().max(254),
  mobile: z.string().trim().max(32).optional(),
  team: z.string().trim().min(1).max(120),
  role: z.string().trim().max(160).optional(),
  employeeNumber: z.string().trim().max(32).optional(),
});

export const ImportEmployeesInput = z.object({
  businessId: z.string().min(1),
  /** Rows as the table holds them; invalid ones are reported, never imported. */
  rows: z
    .array(
      z.object({
        id: z.string().min(1).max(64),
        name: z.string().max(160),
        email: z.string().max(254),
        mobile: z.string().max(32).optional(),
        team: z.string().max(120),
        role: z.string().max(160).optional(),
        employeeNumber: z.string().max(32).optional(),
      }),
    )
    .min(1)
    .max(ONBOARDING_MAX_EMPLOYEE_ROWS),
  /** Raw team value -> display name chosen in the teams step. */
  teamNames: z.record(z.string().max(120), z.string().max(120)).optional(),
  /** New teams become children of this team (the DVH store root). */
  parentTeamId: z.string().min(1).nullish(),
  /** Report what would happen without writing. */
  dryRun: z.boolean().optional(),
  /** Onboarding: empty the draft rows and record the summary in the same call. */
  clearOnboardingDraftRows: z.boolean().optional(),
  fileName: z.string().max(200).nullish(),
});
export type ImportEmployeesInput = z.infer<typeof ImportEmployeesInput>;
